import { Upload, Check } from "lucide-react";
import { useState, useEffect } from "react";
import { useCompanyProfileStore } from "../../store/useCompanyProfileStore";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

type Props = {
  lang?: any;
};

export default function AdminVerificationContent({
  lang,
}: Props) {
  const { profile, packages, fetchCompanyProfile, fetchCompanyPackage, updateCompanyVerification } = useCompanyProfileStore();

  const [selectedPackage, setSelectedPackage] = useState("");
  const [expanded, setExpanded] = useState<string[]>([]);
  const [npwpNumber, setNpwpNumber] = useState("");
  const [npwpFile, setNpwpFile] = useState<File | null>(null);
  const [nib, setNib] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isLocked =
    profile?.package_status === "reviewed" ||
    profile?.package_status === "approved";

  const formatRupiah = (value: number | string) => {
    if (!value) return "Rp 0";
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(Number(value));
  };

  useEffect(() => {
    fetchCompanyProfile();
    fetchCompanyPackage();
  }, []);


  useEffect(() => {
    if (profile) {
      setNpwpNumber(profile.npwp?.toString() || "");
      setNib(profile.nib || "");
    }
  }, [profile]);

  const toggleExpand = (id: string) => {
    if (expanded.includes(id)) {
      setExpanded(expanded.filter(item => item !== id));
    } else {
      setExpanded([...expanded, id]);
    }
  };

  const handleNpwpUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setNpwpFile(file);
  };

  const handleSubmit = async () => {
    if (isSubmitting) return; // cegah double submit

    if (!npwpNumber) {
      toast.error("NPWP number wajib diisi");
      return;
    }

    if (!npwpFile) {
      toast.error("File NPWP wajib diupload");
      return;
    }

    if (!selectedPackage) {
      toast.error("Pilih paket terlebih dahulu");
      return;
    }

    const selectedPkg = packages.find(
      (p) => p.name === selectedPackage
    );

    if (!selectedPkg) {
      toast.error("Paket tidak valid");
      return;
    }

    const payload = {
      npwp: npwpNumber,
      npwp_file: npwpFile,
      nib: nib || "",
      package_id: (selectedPkg.id),
    };

    try {
      setIsSubmitting(true);

      const res = await updateCompanyVerification(payload);

      if (res.success) {
        toast.success(res.message);
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      console.error(err);
      toast.error("Terjadi kesalahan");
    } finally {
      setIsSubmitting(false);
    }
  };


  const displayedPackages = isLocked && profile?.package_id
    ? packages.filter(
      (p) => String(p.id) === String(profile.package_id)
    )
    : packages;

  useEffect(() => {
    if (isLocked && profile?.package_id && packages?.length) {
      const pkg = packages.find(
        (p) => String(p.id) === String(profile.package_id)
      );
      if (pkg) {
        setSelectedPackage(pkg.name);
      }
    }
  }, [isLocked, profile?.package_id, packages]);



  return (
    <div className="mt-6 p-6 bg-white rounded-xl shadow-sm text-gray-700">

      {/* TITLE */}
      <h2 className="text-xl font-semibold text-center mb-8 text-gray-800">
        {lang === "id" ? "Perbaharusi Verifikasi Perusahaan " : "Update Company Verification"}
      </h2>

      {/* NPWP FILE UPLOAD */}
      <div className="mb-6">
        {/* hidden input */}
        <input
          id="npwp-upload"
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={handleNpwpUpload}
          disabled={isLocked}
        />

        <label
          htmlFor={isLocked ? undefined : "npwp-upload"}
          className={`block ${isLocked ? "cursor-not-allowed opacity-60" : "cursor-pointer"
            }`}
        >
          <span className="block mb-2 font-medium">
            {lang === "id"
              ? "Unggah NPWP Perusahaan "
              : "Upload your Company’s NPWP "}<span className="text-red-500">*</span>
          </span>

          <div className="border rounded-lg p-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition">
            <div className="flex items-center gap-3">
              <Upload className="text-primary" size={22} />

              <span className="text-primary font-medium">
                {npwpFile
                  ? npwpFile.name
                  : profile?.npwp_file
                    ? profile.npwp_file.split("/").pop()
                    : "Belum ada file"}
              </span>
            </div>

            <span className="bg-primary text-white px-4 py-2 rounded-full hover:bg-primary/90">
              Upload
            </span>
          </div>
          {profile?.npwp_file && (
            <button
              type="button"
              onClick={() => window.open(profile.npwp_file, "_blank")}
              className="mt-2 text-sm text-primary underline"
            >
              {lang === "id" ? "Lihat File NPWP" : "View NPWP File"}
            </button>
          )}
        </label>

        <p className="text-sm mt-2 text-gray-500">
          (File Type JPG, JPEG, PNG, PDF — Max 2 MB)
        </p>
      </div>

      {/* NPWP Number */}
      <div className="mb-6">
        <label className="block mb-2 font-medium">{lang === "id" ? "Nomor NPWP" : "NPWP Number"} <span className="text-red-500">*</span> </label>
        <input
          type="text"
          className="w-full border p-3 rounded-lg"
          value={npwpNumber}
          onChange={(e) => setNpwpNumber(e.target.value)}
          disabled={isLocked}
        />

      </div>

      {/* NIB */}
      <div className="mb-8">
        <label className="block mb-2 font-medium">{lang === "id" ? "NIB (opsional)" : "NIB (optional)"} </label>
        <input
          type="text"
          className="w-full border p-3 rounded-lg"
          value={nib}
          onChange={(e) => setNib(e.target.value)}
          disabled={isLocked}
        />

      </div>

      {/* PACKAGE SECTION */}
      <div className="mt-10">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          {lang === "id" ? "Pilih Paket Job Posting" : "Select Job Posting Package"}
        </h3>

        <div className="grid md:grid-cols-1 gap-6 mt-6">
          {displayedPackages?.map((p, index) => (
            <div
              key={index}
              className={`relative rounded-2xl border p-6 cursor-pointer shadow-sm transition-all hover:shadow-lg bg-white 
                 ${isLocked
                  ? "cursor-not-allowed opacity-70"
                  : "cursor-pointer hover:shadow-lg"}
                ${selectedPackage === p.name
                  ? "border-primary border-4 shadow-lg"
                  : "border-gray-300"}
                ${selectedPackage === p.name ? "border-primary border-4 shadow-lg" : "border-gray-300"}`}
              onClick={() => setSelectedPackage(p.name)}
            >
              {selectedPackage === p.name && (
                <div className="absolute top-3 right-3 bg-primary text-white text-xs font-semibold px-3 py-1 rounded-full shadow-md">
                  {lang === "id" ? "Dipilih" : "Selected"}
                </div>
              )}
              <h3 className="text-xl font-semibold text-gray-800">{p.name}</h3>

              <p className="text-primary font-bold text-2xl mt-1">
                {formatRupiah(p.price)}
              </p>
              <p className="text-gray-500 text-sm -mt-1">{lang === "id" ? "Jumlah Faktur" : "Total Invoice"} </p>

              <hr className="my-4" />

              <ul className="space-y-2 text-sm">
                <li className="flex gap-2">
                  <Check size={18} className="text-primary" />
                  <span>{p.slot_posting} {lang === "id" ? "Slot Total " : "Total Slot"} ★</span>
                </li>

                <li className="flex gap-2">
                  <Check size={18} className="text-primary" />
                  <span>{p.active_day} {lang === "id" ? "Hari Masa Aktif" : "Active Day"} </span>
                </li>

                <li className="flex gap-2">
                  <Check size={18} className="text-primary" />
                  <span>{lang === "id" ? "Posting Harian:" : "Posting Days:"}  {p.posting_day} {lang === "id" ? "hari" : "Day"} </span>
                </li>
              </ul>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpand(p.name);
                }}
                className="mt-4 text-primary text-sm font-medium underline"
              >
                {expanded.includes(p.name)
                  ? lang === "id"
                    ? "Tampilkan Lebih Sedikit"
                    : "Show Less"
                  : lang === "id"
                    ? "Baca Selengkapnya"
                    : "Read More"}

              </button>

              {expanded.includes(p.name) && (
                <div
                  className="mt-4 text-sm bg-gray-50 border rounded-xl p-4 animate-fadeIn table-pkg"
                  dangerouslySetInnerHTML={{ __html: p.description }}
                />
              )}


              {expanded === p.name && (
                <div
                  className="mt-4 text-sm bg-gray-50 border rounded-xl p-4 animate-fadeIn table-pkg"
                  dangerouslySetInnerHTML={{ __html: p.description }}
                />
              )}
            </div>
          ))}
        </div>

      </div>

      <div className="mt-10 text-center">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || isLocked}
          className={`px-6 py-3 rounded-xl text-white transition
    ${isSubmitting || isLocked
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-primary hover:bg-primary/90"
            }`}
        >
          {profile?.package_status === "reviewed"
            ? lang === "id"
              ? "Verifikasi Sedang Diproses"
              : "Verification in Progress"
            : profile?.package_status === "approved"
              ? lang === "id"
                ? "Terverifikasi"
                : "Verified"
              : isSubmitting
                ? lang === "id"
                  ? "Memproses..."
                  : "Processing..."
                : lang === "id"
                  ? "Perbaharui Verifikasi"
                  : "Update Verification"}

        </button>

      </div>

    </div>
  );
}
